import { de } from '../../i18n'
import type { AccountDetail, DeletionScope } from './types'

/**
 * §7: mirrors `deleteAccount`'s own comparison in
 * apps/api/src/modules/admin/service.ts exactly — trimmed, case-insensitive
 * — so the button enables at precisely the moment the server would accept
 * the request. The server checks again regardless (§7: "eine Bestätigung
 * in der Oberfläche ist keine Zugriffskontrolle"); this only decides
 * whether the button is clickable, never whether the deletion happens.
 */
export function emailConfirmationMatches(typed: string, actualEmail: string): boolean {
  return typed.trim().toLowerCase() === actualEmail.trim().toLowerCase()
}

export interface DeletionSummaryInput {
  firstName: string
  lastName: string
  email: string
  scope: DeletionScope
  /** Company-wide counts (AccountDetail.usage) — only meaningful, and only shown, when `scope === 'company'`. */
  transactions: number
  documents: number
  invoices: number
}

/**
 * `DeleteAccountDialog`'s only path from the fetched `AccountDetail` to
 * `deletionSentence`'s input — in particular, `scope` comes from
 * `detail.deletion.scope` (decided server-side, §6.0) here and nowhere
 * else in the component. Pulled out to its own testable function rather
 * than assembled inline in the .tsx: a hardcoded `scope: 'company'` at the
 * call site would otherwise show every test green, since the dialog itself
 * has no render test — this function is what closes that gap.
 */
export function deletionSummaryInputFromDetail(detail: AccountDetail): DeletionSummaryInput {
  return {
    firstName: detail.person.firstName,
    lastName: detail.person.lastName,
    email: detail.person.email,
    scope: detail.deletion.scope,
    transactions: detail.usage.transactions,
    documents: detail.usage.documents,
    invoices: detail.usage.invoices,
  }
}

/**
 * §7's sentence: names what will be destroyed, with counts, and says which
 * of §6.0's two deletions happens. Built here rather than inline in a
 * component so no German string — and no count formatting — lives in a
 * .tsx file.
 */
export function deletionSentence(input: DeletionSummaryInput): string {
  const name = `${input.firstName} ${input.lastName}`
  return input.scope === 'company'
    ? de.users.delete.companySentence(name, input.email, input.transactions, input.documents, input.invoices)
    : de.users.delete.userSentence(name, input.email)
}

/** The toast shown once the deletion has actually happened — past tense, matching whichever of §6.0's two outcomes occurred. */
export function deletionSuccessMessage(scope: DeletionScope, firstName: string, lastName: string): string {
  const name = `${firstName} ${lastName}`
  return scope === 'company' ? de.users.delete.successCompany(name) : de.users.delete.successUser(name)
}
